import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AlertCircle, CheckCircle, Eye, EyeOff, Lock, Mail, User, Building } from 'lucide-react';
import toast from 'react-hot-toast';
import Footer from './Footer';

interface InvitationData {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    organisationName: string;
}

interface PasswordStrength {
    score: number; // 0-4
    label: string;
    color: string;
}

export default function AcceptInvitationPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    // State management
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [invitationData, setInvitationData] = useState<InvitationData | null>(null);

    // Form state
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({ score: 0, label: '', color: '' });
    const [formErrors, setFormErrors] = useState<{ password?: string; confirmPassword?: string }>({});

    const verifyToken = useCallback(async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://dvla-licence-checker.azurewebsites.net/api'}/invitations/verify/${token}`);

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Invalid or expired invitation');
            }

            const data = await response.json();
            setInvitationData(data.data);
            setError('');
        } catch (err: any) {
            setError(err.message || 'Failed to verify invitation token');
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Verify token on page load
    useEffect(() => {
        if (!token) {
            setError('Invalid invitation link. No token provided.');
            setLoading(false);
            return;
        }

        verifyToken();
    }, [token, verifyToken]);

    // Calculate password strength
    useEffect(() => {
        if (!password) {
            setPasswordStrength({ score: 0, label: '', color: '' });
            return;
        }

        let score = 0;

        // Length check
        if (password.length >= 8) score++;
        if (password.length >= 12) score++;

        // Character variety
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        if (/[^a-zA-Z0-9]/.test(password)) score++;

        // Cap at 4
        score = Math.min(score, 4);

        const strengthMap = [
            { label: 'Very Weak', color: 'bg-red-500' },
            { label: 'Weak', color: 'bg-orange-500' },
            { label: 'Fair', color: 'bg-yellow-500' },
            { label: 'Good', color: 'bg-blue-500' },
            { label: 'Strong', color: 'bg-green-500' }
        ];

        setPasswordStrength({ score, ...strengthMap[score] });
    }, [password]);

    const validateForm = (): boolean => {
        const errors: { password?: string; confirmPassword?: string } = {};

        // Password validation
        if (!password) {
            errors.password = 'Password is required';
        } else if (password.length < 8) {
            errors.password = 'Password must be at least 8 characters';
        } else if (!/[A-Z]/.test(password)) {
            errors.password = 'Password must contain at least one uppercase letter';
        } else if (!/[a-z]/.test(password)) {
            errors.password = 'Password must contain at least one lowercase letter';
        } else if (!/[0-9]/.test(password)) {
            errors.password = 'Password must contain at least one number';
        } else if (!/[^a-zA-Z0-9]/.test(password)) {
            errors.password = 'Password must contain at least one special character';
        }

        // Confirm password validation
        if (!confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (password !== confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        setError('');

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://dvla-licence-checker.azurewebsites.net/api'}/invitations/accept`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    password
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to accept invitation');
            }

            const data = await response.json();

            // Store the JWT token
            localStorage.setItem('auth_token', data.token);

            // Show success message briefly before redirecting
            toast.success('Account activated successfully! Redirecting to dashboard...');

            // Redirect to login page (or dashboard if auto-login is implemented)
            setTimeout(() => {
                window.location.href = '/';
            }, 1000);

        } catch (err: any) {
            setError(err.message || 'Failed to activate account');
        } finally {
            setSubmitting(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <div className="flex-1 flex items-center justify-center">
                    <div className="animate-spin h-12 w-12 border-4 border-t-transparent rounded-full" style={{ borderColor: '#69BE28 transparent transparent transparent' }}></div>
                </div>
                <Footer />
            </div>
        );
    }

    // Error state (invalid/expired token)
    if (error && !invitationData) {
        return (
            <div className="min-h-screen bg-gray-50 flex flex-col">
                <div className="flex-1 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
                    <div className="flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mx-auto mb-4">
                        <AlertCircle className="w-8 h-8 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">Invalid Invitation</h2>
                    <p className="text-center text-gray-600 mb-6">{error}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                        Go to Login
                    </button>
                </div>
                </div>
                <Footer />
            </div>
        );
    }

    // Success state - show form
    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            <div className="flex-1 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center w-16 h-16 rounded-full mx-auto mb-4" style={{ backgroundColor: '#69BE28' }}>
                        <CheckCircle className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Accept Invitation</h1>
                    <p className="text-gray-600">Create your password to activate your account</p>
                </div>

                {/* Invitation Details */}
                {invitationData && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-3">
                        <div className="flex items-center gap-3">
                            <User className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-sm text-gray-500">Name</p>
                                <p className="font-medium text-gray-900">{invitationData.firstName} {invitationData.lastName}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Mail className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-sm text-gray-500">Email</p>
                                <p className="font-medium text-gray-900">{invitationData.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Building className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-sm text-gray-500">Organisation</p>
                                <p className="font-medium text-gray-900">{invitationData.organisationName}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Lock className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-sm text-gray-500">Role</p>
                                <p className="font-medium text-gray-900 capitalize">{invitationData.role}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Password Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Password Field */}
                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                            Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-opacity-50 focus:outline-none ${
                                    formErrors.password 
                                        ? 'border-red-500 focus:ring-red-500' 
                                        : 'border-gray-300 focus:border-[#69BE28] focus:ring-[#69BE28]'
                                }`}
                                placeholder="Enter your password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                        {formErrors.password && (
                            <p className="text-sm text-red-600 mt-1">{formErrors.password}</p>
                        )}

                        {/* Password Strength Indicator */}
                        {password && (
                            <div className="mt-2">
                                <div className="flex items-center gap-2 mb-1">
                                    <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                                            style={{ width: `${(passwordStrength.score / 4) * 100}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-xs font-medium text-gray-600">{passwordStrength.label}</span>
                                </div>
                                <p className="text-xs text-gray-500">
                                    Password must contain: 8+ characters, uppercase, lowercase, number, and special character
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password Field */}
                    <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                            Confirm Password <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-opacity-50 focus:outline-none ${
                                    formErrors.confirmPassword 
                                        ? 'border-red-500 focus:ring-red-500' 
                                        : 'border-gray-300 focus:border-[#69BE28] focus:ring-[#69BE28]'
                                }`}
                                placeholder="Confirm your password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                        </div>
                        {formErrors.confirmPassword && (
                            <p className="text-sm text-red-600 mt-1">{formErrors.confirmPassword}</p>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={submitting || !password || !confirmPassword}
                        className="w-full px-4 py-3 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: '#69BE28' }}
                    >
                        {submitting ? (
                            <span className="flex items-center justify-center gap-2">
                                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
                                Activating Account...
                            </span>
                        ) : (
                            'Activate Account'
                        )}
                    </button>
                </form>

                {/* Footer */}
                <div className="mt-6 text-center">
                    <p className="text-sm text-gray-600">
                        Already have an account?{' '}
                        <button
                            onClick={() => navigate('/login')}
                            className="font-medium hover:underline"
                            style={{ color: '#69BE28' }}
                        >
                            Sign in
                        </button>
                    </p>
                </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}